import { getSupabaseAdminClient } from '@/lib/supabase/admin';

/**
 * `scoring_private.rating_recalc_due`: rated corrections whose ratings still need `recalcRatings`
 * (migration 20261025220000), plus the platform-wide rebuild lease. Service role only, reached
 * through its RPCs.
 */
function client() {
  const c = getSupabaseAdminClient();
  if (!c) throw Error('Supabase client not configured');
  return c;
}

export interface RatingRecalcDue {
  id: string;
  from_at: string;
  attempts: number;
  /** The corrected match's two players: reset from their latest rating after the rebuild. */
  player_ids: string[];
}

export const ratingRecalcDueModel = {
  /** Leases open markers (or just `id`) for 10 minutes; `attempts` includes this claim. */
  async claim(id?: string, limit = 10): Promise<RatingRecalcDue[]> {
    const { data, error } = await client().rpc('rating_recalc_due_claim', {
      p_id: id,
      p_limit: limit,
    });
    if (error) throw error;
    return data ?? [];
  },
  async done(id: string): Promise<void> {
    const { error } = await client().rpc('rating_recalc_due_done', { p_id: id });
    if (error) throw error;
  },
  /** Ends the lease so the next claim retries it. */
  async release(id: string): Promise<void> {
    const { error } = await client().rpc('rating_recalc_due_release', { p_id: id });
    if (error) throw error;
  },
  /** Markers out of tries, each returned once so it alerts once. */
  async takeExhausted(): Promise<Array<{ id: string; from_at: string }>> {
    const { data, error } = await client().rpc('rating_recalc_due_take_exhausted');
    if (error) throw error;
    return data ?? [];
  },
  /** One rebuild at a time: a 6-minute lease for `holder`; the run's start time, or null if held. */
  async acquireRun(holder: string): Promise<string | null> {
    const { data, error } = await client().rpc('rating_recalc_run_acquire', { p_holder: holder });
    if (error) throw error;
    return data ?? null;
  },
  async releaseRun(holder: string): Promise<void> {
    const { error } = await client().rpc('rating_recalc_run_release', { p_holder: holder });
    if (error) throw error;
  },
  /** Queues a rebuild from `fromAt` whose `playerIds` are reset after it; returns the marker id. */
  async enqueue(fromAt: string, playerIds: string[]): Promise<string> {
    const { data, error } = await client().rpc('rating_recalc_due_enqueue', {
      p_from_at: fromAt,
      p_player_ids: playerIds,
    });
    if (error) throw error;
    return data;
  },
  /**
   * A rated save's rebuild, queued only while a rebuild runs or within 6 minutes after one (its read
   * of current_rating may be stale, or it may overwrite the rebuild's); joins an open, unclaimed
   * marker when there is one. Null when no rebuild is in that window.
   */
  async enqueueIfRebuilding(fromAt: string, playerIds: string[]): Promise<string | null> {
    const { data, error } = await client().rpc('rating_recalc_due_enqueue_if_rebuilding', {
      p_from_at: fromAt,
      p_player_ids: playerIds,
    });
    if (error) throw error;
    return data ?? null;
  },
  /** Queues rated rows written since `since` (finalisations during a rebuild); null when none. */
  async enqueueSince(since: string): Promise<string | null> {
    const { data, error } = await client().rpc('rating_recalc_due_enqueue_since', {
      p_since: since,
    });
    if (error) throw error;
    return data ?? null;
  },
};
